import React, { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import { numberToGrade } from '../utils/grades';

interface DraftPageProps {
  leagueId: string;
  userId?: string;
  teamId?: string;
  onDraftComplete?: () => void;
}

interface DraftPick {
  id: string;
  round: number;
  pick_number: number;
  team_name: string;
  team_id: string;
  player_name: string;
  player_id: string;
  position: string;
  overall: number;
  draft_position: number;
}

const DraftPage: React.FC<DraftPageProps> = ({ leagueId, userId, teamId }) => {
  const [picks, setPicks] = useState<DraftPick[]>([]);
  const [loading, setLoading] = useState(true);
  const [draftStatus, setDraftStatus] = useState<string>('loading');
  const [myPosition, setMyPosition] = useState<number | null>(null);
  const [rounds, setRounds] = useState<number>(15);
  const [activeRound, setActiveRound] = useState<number>(1);

  useEffect(() => {
    loadDraft();
    // Poll every 5s during active draft
    const ival = setInterval(loadDraft, 5000);
    return () => clearInterval(ival);
  }, [leagueId]);

  const loadDraft = async () => {
    if (!isSupabaseConfigured() || !supabase) return;
    
    const { data: config } = await supabase.from('draft_config')
      .select('*').eq('league_id', leagueId).single();
    
    if (config) {
      setDraftStatus(config.draft_status);
      setRounds(config.total_rounds || 15);

      // Find my team's draft position
      if (config.draft_order && teamId) {
        const pos = config.draft_order.indexOf(teamId) + 1;
        setMyPosition(pos > 0 ? pos : null);
      }
    }

    // Load picks with player and team data
    const { data: picksData, error } = await supabase
      .from('draft_picks')
      .select('id, round, pick_number, team_id, player_id, status, draft_position, players(first_name, last_name, position, overall), teams(name)')
      .eq('league_id', leagueId)
      .order('pick_number', { ascending: true });

    if (picksData && picksData.length > 0) {
      setPicks(picksData.map((p: any) => ({
        id: p.id,
        round: p.round,
        pick_number: p.pick_number,
        team_name: p.teams?.name || 'Unknown',
        team_id: p.team_id,
        player_name: `${p.players?.first_name || '?'} ${p.players?.last_name || ''}`,
        player_id: p.player_id,
        position: p.players?.position || '?',
        overall: p.players?.overall || 50,
        draft_position: p.draft_position || 0,
      })));
    }

    setLoading(false);
  };

  if (loading) return <div className="draft-loading"><div className="spinner" />Loading Draft...</div>;

  // Locked state
  if (draftStatus === 'not_started' || (draftStatus === 'waiting' && picks.length === 0)) {
    return (
      <div className="draft-page">
        <div className="draft-header">
          <h1>🎲 League Draft</h1>
          <div className="draft-waiting">
            <p>🔒 <strong>Draft Not Started Yet</strong></p>
            <p>The draft priority sheet becomes available once 8+ owners have joined and the 15-minute clock starts.</p>
            <p>Everyone gets the same window — no early access.</p>
          </div>
        </div>
      </div>
    );
  }

  // Group picks by round
  const picksByRound: Record<number, DraftPick[]> = {};
  picks.forEach(p => {
    if (!picksByRound[p.round]) picksByRound[p.round] = [];
    picksByRound[p.round].push(p);
  });

  const myPicks = picks.filter(p => p.team_id === teamId);
  const myBestPick = myPicks.length > 0 ? myPicks[0] : null;

  return (
    <div className="draft-page">
      {/* Header with position banner */}
      <div className="draft-header">
        <h1>🎲 League Draft Results</h1>
        
        {myPosition && (
          <div className="draft-position-banner">
            <div className="position-badge">#{myPosition}</div>
            <div className="position-info">
              <div>Your Draft Position</div>
              <div className="position-detail">
                Pick #{myPosition} in Round 1 (snake: #{myPosition}, {rounds * 2 - myPosition + 1} in R2, ...)
              </div>
            </div>
          </div>
        )}

        {myBestPick && (
          <div className="best-pick-banner">
            ⭐ Round 1, Pick {myBestPick.pick_number}: {myBestPick.player_name} ({myBestPick.position}) — {numberToGrade(myBestPick.overall)}
          </div>
        )}

        <div className="draft-status-bar">
          <span>{picks.length} picks made</span>
          <span>{picks.length === 0 ? 'Waiting...' : 
            draftStatus === 'completed' ? '✅ Draft Complete' : '🔄 Draft in progress'}</span>
        </div>
      </div>

      {/* Round tabs */}
      <div className="draft-rounds-tabs">
        {Array.from({ length: rounds }, (_, i) => i + 1).map(r => (
          <button
            key={r}
            className={`round-tab ${activeRound === r ? 'active' : ''} ${picksByRound[r]?.length ? 'has-picks' : ''}`}
            onClick={() => setActiveRound(r)}
          >
            R{r}
            {picksByRound[r]?.length && <span className="round-count">{picksByRound[r].length}</span>}
          </button>
        ))}
      </div>

      {/* Picks table */}
      {picksByRound[activeRound]?.length > 0 ? (
        <div className="draft-results-table">
          <div className="draft-table-header">
            <span className="col-pick">Pick</span>
            <span className="col-team">Team</span>
            <span className="col-player">Player</span>
            <span className="col-pos">Pos</span>
            <span className="col-ovr">OVR</span>
          </div>
          {picksByRound[activeRound].map(p => {
            const isMyPick = p.team_id === teamId;
            return (
              <div key={p.id} className={`draft-table-row ${isMyPick ? 'my-pick' : ''}`}>
                <span className="col-pick">#{p.pick_number}</span>
                <span className="col-team">
                  <span className="draft-pos-tag">#{p.draft_position}</span>
                  {p.team_name}
                </span>
                <span className="col-player">{p.player_name}</span>
                <span className="col-pos">{p.position}</span>
                <span className="col-ovr">{numberToGrade(p.overall)}</span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="draft-no-picks">No picks in this round yet.</div>
      )}

      {/* My picks summary */}
      {myPicks.length > 0 && (
        <div className="my-picks-summary">
          <h3>🏷️ Your Picks</h3>
          <div className="my-picks-grid">
            {myPicks.map(p => (
              <div key={p.id} className="my-pick-card">
                <div className="mp-round">R{p.round} #{p.pick_number}</div>
                <div className="mp-name">{p.player_name}</div>
                <div className="mp-detail">{p.position} — {numberToGrade(p.overall)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DraftPage;
